import React, { useState } from 'react'
import DOMPurify from 'dompurify'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

import { formatDateShort } from 'utils/dateUtils'
import { getActiveIntegrationLabel } from 'utils/integrations'
import { URL_CONST } from 'utils/urlConsts'

import {
  CREDIT_NOTIFICATION_TYPES,
  CREDIT_NOTIFICATIONS_COPY,
} from 'containers/Agents/creditConstants'
import { REINTEGRATION_MESSAGE_OPTIONS } from 'containers/Navigation/SystemNotifications/constants'

import { CreditsAlt } from 'components/lib/DesignSystem/Icons'
import {
  getPasswordExpiryCopy,
  hasPasswordExpiryWarning,
} from 'components/pages/navigation/topBar/notificationSpecs'

import NavigationItem from './NavigationItem'

/**
 * Resolve the credit-notification subtext for the active variant.
 * Backend payload shape (best-effort, falls back to the PLG_EXHAUSTED copy):
 *   notifications.ai_credits = {
 *     type: 'plg_exhausted' | 'approaching_limit' | 'credit_limit_reached',
 *     billing_model: 'pay_as_you_go' | 'prepurchase',
 *     reset_date_label: string,
 *     expiration_date_label: string,
 *   }
 */
export const resolveCreditNotification = (notif, resetLabel) => {
  if (!notif?.type) return null
  if (notif.type === CREDIT_NOTIFICATION_TYPES.CREDITS_RESTORED) return null
  const variantCopy = CREDIT_NOTIFICATIONS_COPY[notif.type]
  if (!variantCopy) return null
  const params = {
    resetDateLabel: notif.reset_date_label,
    expirationDateLabel: notif.expiration_date_label,
  }
  if (notif.type === CREDIT_NOTIFICATION_TYPES.PLG_EXHAUSTED) {
    if (!variantCopy.cta) return null
    return {
      title: variantCopy.title,
      subtext: variantCopy.subtext(params),
      cta: variantCopy.cta,
      // selectNextResetLabel ("Aug 1st"). Own row, not part of the body sentence.
      resetLine: resetLabel ? `Preview credits reset on: ${resetLabel}.` : null,
    }
  }
  const subVariant =
    notif.billing_model === 'prepurchase' ? variantCopy.prepurchase : variantCopy.payAsYouGo
  if (!subVariant?.cta) return null
  return {
    title: subVariant.title,
    subtext: subVariant.subtext(params),
    cta: subVariant.cta,
  }
}

const PSIMA_LAST_OSINT_RUN_CARDS_SUMMARY_NOTIFICATION_KEY = 'psima_last_osint_run_cards_summary'
const PSIMA_SIMULATIONS_LAUNCH_AND_DELIVERY_NOTIFICATION_KEY =
  'psima_simulations_launch_and_delivery'

function SystemNotificationsModal({
  className,
  setOnClickCallback,
  setBadgeCount,
  data,
  title,
  integrations,
  onCloseCallback,
  dismissNotification,
  isBillingDisabled,
  creditResetLabel,
}) {
  const [hasClickedDeepfakeLink, setHasClickedDeepfakeLink] = useState(false)
  const [hasDismissedLatestGeneratedCards, setHasDismissedLatestGeneratedCards] = useState(false)
  const [dismissedLaunchDeliveryIds, setDismissedLaunchDeliveryIds] = useState([])

  const notifications = data?.notifications || {}

  const getCount = () => {
    const {
      license_poc,
      license_expired,
      license_warn,
      license_limit_reached,
      mailbox_911_disable_reason,
      warn_days_to_password_expire,
      re_integration_messages,
      deepfake,
      ai_credits,
      psima_latest_generated_cards,
      psima_simulations_launch_and_delivery,
    } = notifications

    let counter = 0

    if (!license_poc) {
      if (license_expired) counter++
      else if (license_warn) counter++
    }

    if (license_limit_reached) counter++
    if (mailbox_911_disable_reason) counter++
    if (hasPasswordExpiryWarning(warn_days_to_password_expire)) counter++
    if (re_integration_messages?.length) counter += re_integration_messages.length
    if (deepfake?.suspicious_meetings_since_last_visit_timestamp && !hasClickedDeepfakeLink)
      counter++
    if (!isBillingDisabled && resolveCreditNotification(ai_credits, creditResetLabel)) counter++
    if (psima_latest_generated_cards?.total_cards_count > 0 && !hasDismissedLatestGeneratedCards)
      counter++
    counter += (psima_simulations_launch_and_delivery || []).filter(
      (entry) => !dismissedLaunchDeliveryIds.includes(entry.id)
    ).length

    return counter
  }

  const {
    license_poc,
    license_expired,
    login_expiry,
    license_warn,
    license_expiery,
    mailbox_911_disable_reason,
    warn_days_to_password_expire,
    license_limit_reached,
    re_integration_messages,
    deepfake,
    ai_credits,
    psima_latest_generated_cards,
    psima_simulations_launch_and_delivery,
  } = notifications

  // The credit entry ends in an "Add more credits" purchase CTA — a money
  // surface, so it must stay hidden for POV / billing-bypassed / PAYG-disabled
  // tenants even if the backend still populates notifications.ai_credits
  // (see selectIsBillingDisabled).
  const aiCreditsNotif = isBillingDisabled
    ? null
    : resolveCreditNotification(ai_credits, creditResetLabel)

  const counter = getCount()

  return (
    <NavigationItem
      className={className}
      setOnClickCallback={setOnClickCallback}
      setBadgeCount={setBadgeCount(getCount())}
      width="305px"
      onCloseCallback={onCloseCallback}
      content={
        <>
          <div className="notif-title">{title}</div>
          {!license_poc && (
            <>
              {license_expired && (
                <div className="notif-container">
                  Your license has expired. Starting from {formatDateShort(login_expiry)} you will
                  not be able to login. Please, contact us at{' '}
                  <a href="mailto:support@ironscales.com">support@ironscales.com</a>.
                </div>
              )}
              {license_warn && (
                <div className="notif-container">
                  Your license will expire on {formatDateShort(license_expiery)}. Please contact us
                  at <a href="mailto:support@ironscales.com">support@ironscales.com</a> to extend
                  your license.
                </div>
              )}
            </>
          )}
          {hasPasswordExpiryWarning(warn_days_to_password_expire) && (
            <div className="notif-container">
              {warn_days_to_password_expire <= 0
                ? getPasswordExpiryCopy(warn_days_to_password_expire).text
                : `Your Password Will Expire In ${warn_days_to_password_expire} Days`}
              <br />
              <a href={URL_CONST.settings.profile}>Change it</a>.
            </div>
          )}
          {license_limit_reached && (
            <div className="notif-container">
              You have more mailboxes in your
              {` ${getActiveIntegrationLabel(integrations)} `}
              account than in your IRONSCALES license limit.
              <br /> Please <a href="mailto:licenses@ironscales.com">contact us</a> to increase the
              number of protected mailboxes.
            </div>
          )}
          {mailbox_911_disable_reason && (
            <div className="notif-container">
              Your 911 mailbox is currently disabled. Please reconfigure the 911 mailbox in the
              Settings section. Contact us at{' '}
              <a href="mailto:support@ironscales.com">support@ironscales.com</a> for further
              assistance.
            </div>
          )}
          {re_integration_messages?.map((messageId) => {
            const messageHtml = REINTEGRATION_MESSAGE_OPTIONS[messageId]
            if (!messageHtml) return null

            return (
              <div
                key={messageId}
                className="notif-container"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(messageHtml),
                }}
              />
            )
          })}
          {deepfake?.suspicious_meetings_since_last_visit_timestamp && !hasClickedDeepfakeLink && (
            <div className="notif-container">
              New deepfake activity since your last visit!
              <br />
              <Link
                to={`${URL_CONST.deepfake.meetings}?notification=suspicious&timestamp=${deepfake.suspicious_meetings_since_last_visit_timestamp}`}
                onClick={() => setHasClickedDeepfakeLink(true)}
              >
                Investigate
              </Link>
            </div>
          )}
          {aiCreditsNotif && (
            <div className="notif-container ai-credits-notif" id="ai-credits-bell-notification">
              <div className="ai-credits-notif-main">
                <span className="ai-credits-notif-icon" aria-hidden>
                  <CreditsAlt width="20px" height="20px" fill="var(--ds-v3-icon-button-color)" />
                </span>
                <div className="ai-credits-notif-body">
                  <span className="ai-credits-notif-title">{aiCreditsNotif.title}</span>
                  <br />
                  {aiCreditsNotif.subtext}
                  {aiCreditsNotif.resetLine && (
                    <div className="ai-credits-notif-reset">{aiCreditsNotif.resetLine}</div>
                  )}
                </div>
                <span className="ai-credits-notif-unread" aria-hidden />
              </div>
              <Link className="ai-credits-notif-cta" to={URL_CONST.settings.creditsBilling}>
                {aiCreditsNotif.cta}
              </Link>
            </div>
          )}
          {psima_latest_generated_cards?.total_cards_count > 0 &&
            !hasDismissedLatestGeneratedCards && (
              <div className="notif-container">
                {`OSINT Scan complete — ${psima_latest_generated_cards.total_cards_count} new simulations ready (${psima_latest_generated_cards.level_counts.high}H / ${psima_latest_generated_cards.level_counts.medium}M / ${psima_latest_generated_cards.level_counts.low}L). Launch while they're current.`}
                <br />
                <Link
                  to={URL_CONST.psima.self}
                  onClick={() => {
                    dismissNotification({
                      notification_key: PSIMA_LAST_OSINT_RUN_CARDS_SUMMARY_NOTIFICATION_KEY,
                      reference: psima_latest_generated_cards.dismiss_reference,
                    })
                    setHasDismissedLatestGeneratedCards(true)
                  }}
                >
                  Review & launch
                </Link>
              </div>
            )}
          {(psima_simulations_launch_and_delivery || [])
            .filter((entry) => !dismissedLaunchDeliveryIds.includes(entry.id))
            .map((entry) => {
              const single = entry.campaign_count === 1
              const message = single
                ? `${entry.title} is live — delivered to ${entry.delivered}/${entry.total}${
                    entry.failed > 0
                      ? `, ${entry.failed} couldn't be delivered (credits refunded)`
                      : ''
                  }. Tracking is open.`
                : `${entry.campaign_count} simulations are live — ${entry.delivered}/${
                    entry.total
                  } delivered${
                    entry.failed > 0 ? `, ${entry.failed} failed (credits refunded)` : ''
                  }.`

              return (
                <div className="notif-container" key={entry.id}>
                  {message}
                  <br />
                  <Link
                    to={URL_CONST.psima.launched}
                    onClick={(event) => {
                      // Stop the click from bubbling to NavigationItem's wrapper div, which
                      // would otherwise close the whole dropdown instead of just this entry.
                      event.stopPropagation()
                      dismissNotification({
                        notification_key: PSIMA_SIMULATIONS_LAUNCH_AND_DELIVERY_NOTIFICATION_KEY,
                        reference: entry.id,
                      })
                      setDismissedLaunchDeliveryIds((ids) => [...ids, entry.id])
                    }}
                  >
                    View
                  </Link>
                </div>
              )
            })}
          {counter === 0 && <div className="notif-container">No Notifications</div>}
        </>
      }
    />
  )
}

export default styled(SystemNotificationsModal)`
  .notif-title {
    color: var(--primary-color);
    font-weight: 600;
    font-size: 12px;
    padding: 15px 15px 15px 35px;
    border-bottom: 1px solid var(--color-border);
    word-break: break-word;
  }
  .notif-container {
    color: var(--primary-color);
    font-size: 11px;
    padding: 10px 35px;
    border-bottom: 1px solid var(--color-border);
    line-height: 1.5;
    &:last-child {
      border-bottom: none;
    }
    a {
      color: var(--secondary-color);
      font-size: 11px;
    }
  }
  .ai-credits-notif {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
  }
  .ai-credits-notif-main {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    width: 100%;
  }
  .ai-credits-notif-icon {
    flex-shrink: 0;
    display: inline-flex;
  }
  .ai-credits-notif-body {
    flex: 1;
    min-width: 0;
    color: var(--sub-text-color);
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
  }
  .ai-credits-notif-title {
    font-weight: 600;
    color: var(--notification-credit-title-color);
  }
  .ai-credits-notif-reset {
    color: var(--notification-credit-reset-color);
    font-weight: 400;
  }
  .ai-credits-notif a.ai-credits-notif-cta {
    color: var(--ds-blue-2);
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
  }
  .ai-credits-notif-unread {
    flex-shrink: 0;
    align-self: center;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--negative-color);
  }
`
